import * as React from 'react';

import { formatForShare } from '@/components/ChatBot/utils';
import type {
  AskNeedlState,
  SearchWithinType,
} from '@/features/ChatBotV2/context';
import { NEEDL_UNIVERSE_ID } from '@/features/ChatBotV2/context';
import {
  type AttachmentStatusType,
  type AttachmentType,
  type BotMessageState,
  type MetadataType,
  type SessionState,
  isBotMessageState,
} from '@/features/ChatBotV2/types';
import type { BoardFileType } from '@/features/CustomFeedV2/common';
import type { ReportInfoType } from '@/features/NeedlReport/types';

import type { AskNeedlResponseType, IntermediateStepType } from './useAskNeedl';

export const useAskNeedlState = (initialState: AskNeedlState) => {
  return React.useReducer(askNeedlReducer, initialState);
};

export const askNeedlReducer = (
  state: AskNeedlState,
  action: AskNeedlActions
) => {
  switch (action.type) {
    case 'SET_SEARCH_WITHIN':
      return {
        ...state,
        searchWithin: action.payload,
      };

    case 'SET_PRO': {
      const { sessions } = state;
      const finalProValue = action.payload.finalProValue;

      const newSessions = Object.keys(sessions).reduce((acc, sessionType) => {
        const sessionEntries = sessions[sessionType as keyof typeof sessions];

        const updatedEntries = Object.keys(sessionEntries).reduce(
          (sessionAcc, sessionId) => {
            const currentSession = sessionEntries[sessionId];

            return {
              ...sessionAcc,
              [sessionId]: {
                ...currentSession,
                pro:
                  currentSession.messages.length === 0
                    ? finalProValue
                    : currentSession.pro,
              },
            };
          },
          {}
        );

        return {
          ...acc,
          [sessionType]: updatedEntries,
        };
      }, {} as typeof sessions);

      return {
        ...state,
        sessions: newSessions,
      };
    }

    case 'TOGGLE_WEB_SEARCH': {
      const currentFeedOrDocumentId = state.currentFeedOrDocumentId;
      const searchWithin = state.searchWithin;

      return {
        ...state,
        webSearch: !state.webSearch,
        sessions: {
          ...state.sessions,
          feed: {
            ...state.sessions.feed,
            [NEEDL_UNIVERSE_ID]: {
              messages: [],
              pro: state.sessions[searchWithin][currentFeedOrDocumentId].pro,
              metadata: {
                type: NEEDL_UNIVERSE_ID,
                board_id: NEEDL_UNIVERSE_ID,
              },
            } as SessionState,
          },
        },
      };
    }

    case 'SET_REPORT_MODE': {
      return {
        ...state,
        report: action.payload
          ? { ...state.report, enabled: true }
          : { enabled: false },
      };
    }

    case 'SET_REPORT_INFO': {
      if (!state.report.enabled) return state;

      return {
        ...state,
        report: {
          ...state.report,
          info: action.payload,
        },
      };
    }

    case 'SET_REPORT_SOURCES': {
      if (!state.report.enabled) return state;

      return {
        ...state,
        report: {
          ...state.report,
          sources: action.payload,
        },
      };
    }

    case 'SET_SESSION': {
      const { newSession, searchWithin, id, lastActiveSessionId } =
        action.payload;

      return {
        ...state,
        isLoading: false,
        isStreaming: false,
        lastActiveSessionId,
        webSearch: id === NEEDL_UNIVERSE_ID,
        sessions: {
          ...state.sessions,
          [searchWithin]: {
            ...state.sessions[searchWithin],
            [id]: newSession as SessionState,
          },
        },
      };
    }

    case 'SET_CURRENT_FEED_OR_DOCUMENT_ID': {
      const { searchWithin, currentFeedOrDocumentId, pro, metadata } =
        action.payload;

      return {
        ...state,
        isLoading: false,
        isStreaming: false,
        searchWithin,
        currentFeedOrDocumentId,
        sessions: {
          ...state.sessions,
          [searchWithin]: {
            ...state.sessions[searchWithin],
            [currentFeedOrDocumentId]: state.sessions[searchWithin][
              currentFeedOrDocumentId
            ] || {
              messages: [],
              pro,
              metadata,
            },
          },
        },
      };
    }

    case 'SET_ATTACHMENTS_FEED_ID': {
      const { feedId } = action.payload;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [state.searchWithin]: {
            ...state.sessions[state.searchWithin],
            [state.currentFeedOrDocumentId]: {
              ...state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ],
              attachmentsFeedId: feedId,
            } as SessionState,
          },
        },
      };
    }

    case 'SET_ATTACHMENTS_STATUS': {
      const { attachmentIds, status, error } = action.payload;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [state.searchWithin]: {
            ...state.sessions[state.searchWithin],
            [state.currentFeedOrDocumentId]: {
              ...state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ],
              attachments: state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ].attachments?.map((attachment) => {
                if (attachmentIds.includes(attachment.id)) {
                  return {
                    ...attachment,
                    status,
                    ...(error ? { error } : {}),
                  };
                }
                return attachment;
              }),
            } as SessionState,
          },
        },
      };
    }

    case 'ADD_ATTACHMENT_BUCKET_KEY': {
      const { attachmentId, bucket, key } = action.payload;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [state.searchWithin]: {
            ...state.sessions[state.searchWithin],
            [state.currentFeedOrDocumentId]: {
              ...state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ],
              attachments: state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ].attachments?.map((attachment) => {
                if (attachment.id === attachmentId) {
                  return {
                    ...attachment,
                    bucket,
                    key,
                  };
                }
                return attachment;
              }),
            } as SessionState,
          },
        },
      };
    }

    case 'SET_SESSION_INTERMEDIATE_STEPS': {
      const { botMessageId, prompt, intermediateSteps } = action.payload;

      const botMessageIndex = state.sessions[state.searchWithin][
        state.currentFeedOrDocumentId
      ].messages.findIndex(
        (message) =>
          message.prompt === prompt &&
          message.type === 'bot' &&
          message.id === botMessageId
      );

      if (botMessageIndex === -1) {
        return state;
      }

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [state.searchWithin]: {
            ...state.sessions[state.searchWithin],
            [state.currentFeedOrDocumentId]: {
              ...state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ],
              messages: state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ].messages.map((message) => {
                if (message.id === botMessageId) {
                  return {
                    ...message,
                    intermediateSteps,
                  };
                }
                return message;
              }),
            } as SessionState,
          },
        },
      };
    }

    case 'SET_SESSION_AFTER_COMPLETE': {
      const { botMessageId, prompt, askNeedlData, onComplete } = action.payload;
      const currentFeedOrDocumentId = state.currentFeedOrDocumentId;
      const searchWithin = state.searchWithin;

      if (askNeedlData.answer_type !== 'SEARCH') {
        delete askNeedlData.retrieved_results;
      }

      const botMessageIndex = state.sessions[searchWithin][
        currentFeedOrDocumentId
      ].messages.findIndex(
        (message) =>
          message.prompt === prompt &&
          message.type === 'bot' &&
          message.id === botMessageId
      );

      const answerFragments = askNeedlData.generated_answer
        ? (askNeedlData.generated_answer.sentences.reduce(
            (acc: string[], sentenceInfo) => {
              const { sentence, citations } = sentenceInfo;
              return [
                ...acc,
                sentence,
                ...citations.map((citation) => {
                  const urlSearchParams = new URLSearchParams();
                  if (citation.document_id) {
                    urlSearchParams.append('document_id', citation.document_id);
                    urlSearchParams.append('access_key', citation.access_key);
                    urlSearchParams.append('source', citation.source);
                    urlSearchParams.append(
                      'category_tab',
                      citation.category_tab
                    );
                    if (citation.channel_id) {
                      urlSearchParams.append(
                        'channel_id',
                        citation.channel_id as string
                      );
                    }

                    if (citation.mime_type) {
                      urlSearchParams.append('mime_type', citation.mime_type);
                    }

                    if (citation.document_highlight) {
                      urlSearchParams.append(
                        'document_highlight',
                        JSON.stringify(citation.document_highlight)
                      );
                    }
                  } else if (citation.url) {
                    urlSearchParams.append(
                      'link',
                      encodeURIComponent(citation.url)
                    );
                  }

                  urlSearchParams.append('title_label', citation.title_label);
                  urlSearchParams.append('source_label', citation.source_label);
                  urlSearchParams.append('unix', citation.unix);
                  urlSearchParams.append(
                    'content_only',
                    citation.content_only ||
                      'Click the "View Source" button to view the original content.'
                  );
                  urlSearchParams.append(
                    'highlight_indexes',
                    JSON.stringify(citation.highlight_indexes || [])
                  );
                  urlSearchParams.append(
                    'context',
                    encodeURIComponent(citation.context)
                  );

                  if (state.report.enabled) {
                    urlSearchParams.append(
                      'report_type',
                      state.report.info?.id || ''
                    );
                  }

                  const route = citation.document_id ? '/preview' : '/redirect';
                  return `[${
                    citation.id
                  }](${`${route}?${urlSearchParams.toString()}`})`;
                }),
              ];
            },
            []
          ) as string[])
        : [];

      const answerMarkdown = answerFragments.join(' ');

      if (botMessageIndex !== -1) {
        const updatedSessionState: SessionState = {
          ...state.sessions[searchWithin][currentFeedOrDocumentId],
          sessionId: askNeedlData.session_id,
          messages: state.sessions[searchWithin][
            currentFeedOrDocumentId
          ].messages.map((message) =>
            message.id === botMessageId
              ? ({
                  ...message,
                  data: askNeedlData,
                  streamingAnswer: '',
                  answerMarkdown,
                  formattedData: formatForShare(prompt, askNeedlData, 'text'),
                } as BotMessageState)
              : message
          ),
        };

        if (onComplete) {
          onComplete(updatedSessionState);
        }

        return {
          ...state,
          isLoading: false,
          isStreaming: false,
          sessions: {
            ...state.sessions,
            [searchWithin]: {
              ...state.sessions[searchWithin],
              [currentFeedOrDocumentId]: updatedSessionState,
            },
          },
        };
      }

      return state;
    }

    case 'SET_SESSION_WHILE_STREAMING': {
      const { botMessageId, prompt, streamingAnswer } = action.payload;
      const session = state.searchWithin;
      const currentFeedOrDocumentId = state.currentFeedOrDocumentId;
      const botMessageIndex = state.sessions[session][
        currentFeedOrDocumentId
      ].messages.findIndex(
        (message) =>
          message.prompt === prompt &&
          message.type === 'bot' &&
          message.id === botMessageId
      );

      if (botMessageIndex === -1) {
        return state;
      }

      return {
        ...state,
        isLoading: false,
        isStreaming: true,
        sessions: {
          ...state.sessions,
          [session]: {
            ...state.sessions[session],
            [currentFeedOrDocumentId]: {
              ...state.sessions[session][currentFeedOrDocumentId],
              messages: state.sessions[session][
                currentFeedOrDocumentId
              ].messages.map((message) => {
                if (isBotMessageState(message) && message.id === botMessageId) {
                  return {
                    ...message,
                    ...(!message.streamingAnswer
                      ? {
                          intermediateSteps: message.intermediateSteps.map(
                            (step) => ({
                              ...step,
                              loading: false,
                            })
                          ),
                        }
                      : {}),
                    streamingAnswer,
                  };
                }
                return message;
              }),
            } as SessionState,
          },
        },
      };
    }

    case 'SET_SESSION_ON_CLEAR': {
      return {
        ...state,
        isLoading: false,
        isStreaming: false,
        lastActiveSessionId: null,
        sessions: {
          ...state.sessions,
          [state.searchWithin]: {
            ...state.sessions[state.searchWithin],
            [state.currentFeedOrDocumentId]: {
              ...state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ],
              messages: [],
              sessionId: undefined,
              promptEnabled: false,
              attachments: [],
              attachmentsFeedId: undefined,
              searchWithin: state.searchWithin,
            } as SessionState,
          },
        },
      };
    }

    case 'SET_SESSION_ON_QUESTION': {
      const prompt = action.payload as string;
      const userMessageId = crypto.randomUUID() as string;
      const botMessageId = crypto.randomUUID() as string;

      return {
        ...state,
        isLoading: true,
        isStreaming: false,
        sessions: {
          ...state.sessions,
          [state.searchWithin]: {
            ...state.sessions[state.searchWithin],
            [state.currentFeedOrDocumentId]: {
              ...state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ],
              messages: [
                ...state.sessions[state.searchWithin][
                  state.currentFeedOrDocumentId
                ].messages,
                {
                  type: 'user',
                  id: userMessageId,
                  botMessageId: botMessageId,
                  prompt,
                  ...(state.sessions[state.searchWithin][
                    state.currentFeedOrDocumentId
                  ].attachments?.length
                    ? {
                        attachments:
                          state.sessions[state.searchWithin][
                            state.currentFeedOrDocumentId
                          ].attachments,
                      }
                    : {}),
                },
                {
                  type: 'bot',
                  id: botMessageId,
                  data: null,
                  formattedData: '',
                  prompt,
                  streamingAnswer: '',
                  intermediateSteps: [],
                },
              ],
              attachments: [],
            } as SessionState,
          },
        },
      };
    }

    case 'SET_SESSION_ON_FILES_DROP': {
      const { attachments } = action.payload;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [state.searchWithin]: {
            ...state.sessions[state.searchWithin],
            [state.currentFeedOrDocumentId]: {
              ...state.sessions[state.searchWithin][
                state.currentFeedOrDocumentId
              ],
              attachments: [
                ...(state.sessions[state.searchWithin][
                  state.currentFeedOrDocumentId
                ].attachments || []),
                ...attachments,
              ],
            } as SessionState,
          },
        },
      };
    }

    case 'REMOVE_ATTACHMENT': {
      const { attachmentId } = action.payload;
      const currentFeedOrDocumentId = state.currentFeedOrDocumentId;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [state.searchWithin]: {
            ...state.sessions[state.searchWithin],
            [currentFeedOrDocumentId]: {
              ...state.sessions[state.searchWithin][currentFeedOrDocumentId],
              attachments: state.sessions[state.searchWithin][
                currentFeedOrDocumentId
              ].attachments?.filter(
                (attachment) => attachment.id !== attachmentId
              ),
            } as SessionState,
          },
        },
      };
    }

    case 'UPDATE_ATTACHMENT_PROGRESS': {
      const { attachmentId, progress } = action.payload;
      const currentFeedOrDocumentId = state.currentFeedOrDocumentId;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          [state.searchWithin]: {
            ...state.sessions[state.searchWithin],
            [currentFeedOrDocumentId]: {
              ...state.sessions[state.searchWithin][currentFeedOrDocumentId],
              attachments: state.sessions[state.searchWithin][
                currentFeedOrDocumentId
              ].attachments?.map((attachment) => {
                if (attachment.id === attachmentId) {
                  return {
                    ...attachment,
                    progress,
                  };
                }
                return attachment;
              }),
            } as SessionState,
          },
        },
      };
    }

    case 'STOP_SESSION_WHILE_STREAMING': {
      const { botMessageId, prompt } = action.payload;
      const session = state.searchWithin;
      const currentFeedOrDocumentId = state.currentFeedOrDocumentId;
      const botMessageIndex = state.sessions[session][
        currentFeedOrDocumentId
      ].messages.findIndex(
        (message) =>
          message.prompt === prompt &&
          message.type === 'bot' &&
          message.id === botMessageId
      );

      if (botMessageIndex !== -1) {
        return {
          ...state,
          isLoading: false,
          isStreaming: false,
          sessions: {
            ...state.sessions,
            [session]: {
              ...state.sessions[session],
              [currentFeedOrDocumentId]: {
                ...state.sessions[session][currentFeedOrDocumentId],
                messages: state.sessions[session][
                  currentFeedOrDocumentId
                ].messages.map((message) => {
                  if (message.id === botMessageId) {
                    return {
                      ...message,
                      generatingStopped: true,
                    };
                  }
                  return message;
                }),
              } as SessionState,
            },
          },
        };
      }
      return {
        ...state,
        isLoading: false,
        isStreaming: false,
      };
    }

    case 'SET_LAST_ACTIVE_SESSION': {
      const { lastActiveSessionId } = action.payload;

      return {
        ...state,
        lastActiveSessionId,
      };
    }

    case 'START_NEW_SESSION_WITH_HOME_BOARD': {
      const { pro } = action.payload;

      return {
        ...state,
        isLoading: false,
        isStreaming: false,
        webSearch: false,
        lastActiveSessionId: null,
        searchWithin: 'feed' as SearchWithinType,
        currentFeedOrDocumentId: 'home',
        sessions: {
          ...state.sessions,
          feed: {
            ...state.sessions.feed,
            home: {
              messages: [],
              pro,
              attachments: [],
              metadata: {
                type: 'home',
                board_id: 'home',
              } as MetadataType,
            },
          },
        },
      };
    }

    case 'CHANGE_CURRENT_SESSION': {
      const { currentFeedOrDocumentId, searchWithin } = action.payload;

      return {
        ...state,
        currentFeedOrDocumentId,
        searchWithin,
      };
    }

    default:
      return state;
  }
};

export type AskNeedlActions =
  | {
      type: 'ADD_ATTACHMENT_BUCKET_KEY';
      payload: {
        attachmentId: string;
        bucket: string;
        key: string;
      };
    }
  | {
      type: 'CHANGE_CURRENT_SESSION';
      payload: {
        currentFeedOrDocumentId: string;
        searchWithin: SearchWithinType;
      };
    }
  | {
      type: 'REMOVE_ATTACHMENT';
      payload: {
        attachmentId: string;
      };
    }
  | {
      type: 'REMOVE_ATTACHMENT';
      payload: {
        attachmentId: string;
      };
    }
  | {
      type: 'SET_ATTACHMENTS_FEED_ID';
      payload: {
        feedId: string;
      };
    }
  | {
      type: 'SET_ATTACHMENTS_STATUS';
      payload: {
        attachmentIds: string[];
        status: AttachmentStatusType;
        error?: string;
      };
    }
  | {
      type: 'SET_ATTACHMENTS_STATUS';
      payload: {
        attachmentIds: string[];
        status: AttachmentStatusType;
        error?: string;
      };
    }
  | {
      type: 'SET_CURRENT_FEED_OR_DOCUMENT_ID';
      payload: {
        searchWithin: SearchWithinType;
        currentFeedOrDocumentId: string;
        pro: boolean;
        metadata: MetadataType;
      };
    }
  | {
      type: 'SET_EXISTING_SESSION';
      payload: {
        session: SessionState;
        searchWithin: SearchWithinType;
        id: string;
      };
    }
  | {
      type: 'SET_LAST_ACTIVE_SESSION';
      payload: {
        lastActiveSessionId: string | null;
      };
    }
  | {
      type: 'SET_PRO';
      payload: {
        finalProValue: boolean;
      };
    }
  | {
      type: 'SET_REPORT_MODE';
      payload: boolean;
    }
  | {
      type: 'SET_SEARCH_WITHIN';
      payload: SearchWithinType;
    }
  | {
      type: 'SET_SESSION_AFTER_COMPLETE';
      payload: {
        botMessageId: string;
        prompt: string;
        askNeedlData: AskNeedlResponseType;
        onComplete?: (session: SessionState) => void;
      };
    }
  | {
      type: 'SET_SESSION_INTERMEDIATE_STEPS';
      payload: {
        botMessageId: string;
        prompt: string;
        intermediateSteps: IntermediateStepType[];
      };
    }
  | {
      type: 'SET_SESSION_ON_CLEAR';
      payload: {
        searchWithin: SearchWithinType;
      };
    }
  | {
      type: 'SET_SESSION_ON_FILES_DROP';
      payload: {
        attachments: AttachmentType[];
      };
    }
  | {
      type: 'SET_SESSION_ON_QUESTION';
      payload: string;
    }
  | {
      type: 'SET_SESSION_WHILE_STREAMING';
      payload: {
        botMessageId: string;
        prompt: string;
        streamingAnswer: string;
      };
    }
  | {
      type: 'SET_SESSION';
      payload: {
        newSession: SessionState;
        searchWithin: SearchWithinType;
        id: string;
        lastActiveSessionId: string | null;
      };
    }
  | {
      type: 'START_NEW_SESSION_WITH_HOME_BOARD';
      payload: {
        pro: boolean;
      };
    }
  | {
      type: 'STOP_SESSION_WHILE_STREAMING';
      payload: {
        botMessageId: string;
        prompt: string;
      };
    }
  | {
      type: 'TOGGLE_WEB_SEARCH';
    }
  | {
      type: 'UPDATE_ATTACHMENT_PROGRESS';
      payload: {
        attachmentId: string;
        progress: number;
      };
    }
  | { type: 'SET_REPORT_INFO'; payload: ReportInfoType }
  | { type: 'SET_REPORT_SOURCES'; payload: BoardFileType[] };

export const ASK_NEEDL_ACTIONS = {
  SET_SEARCH_WITHIN: 'SET_SEARCH_WITHIN',
  SET_PRO: 'SET_PRO',
  TOGGLE_WEB_SEARCH: 'TOGGLE_WEB_SEARCH',
  SET_SESSION_INTERMEDIATE_STEPS: 'SET_SESSION_INTERMEDIATE_STEPS',
  SET_SESSION: 'SET_SESSION',
  SET_SESSION_AFTER_COMPLETE: 'SET_SESSION_AFTER_COMPLETE',
  SET_SESSION_WHILE_STREAMING: 'SET_SESSION_WHILE_STREAMING',
  SET_SESSION_ON_CLEAR: 'SET_SESSION_ON_CLEAR',
  SET_SESSION_ON_QUESTION: 'SET_SESSION_ON_QUESTION',
  SET_CURRENT_FEED_OR_DOCUMENT_ID: 'SET_CURRENT_FEED_OR_DOCUMENT_ID',
  SET_ATTACHMENTS_FEED_ID: 'SET_ATTACHMENTS_FEED_ID',
  STOP_SESSION_WHILE_STREAMING: 'STOP_SESSION_WHILE_STREAMING',
  SET_REPORT_MODE: 'SET_REPORT_MODE',
  SET_REPORT_INFO: 'SET_REPORT_INFO',
  SET_REPORT_SOURCES: 'SET_REPORT_SOURCES',
  SET_EXISTING_SESSION: 'SET_EXISTING_SESSION',
  SET_LAST_ACTIVE_SESSION: 'SET_LAST_ACTIVE_SESSION',
  START_NEW_SESSION_WITH_HOME_BOARD: 'START_NEW_SESSION_WITH_HOME_BOARD',
  SET_SESSION_ON_FILES_DROP: 'SET_SESSION_ON_FILES_DROP',
  REMOVE_ATTACHMENT: 'REMOVE_ATTACHMENT',
  UPDATE_ATTACHMENT_PROGRESS: 'UPDATE_ATTACHMENT_PROGRESS',
  SET_ATTACHMENTS_STATUS: 'SET_ATTACHMENTS_STATUS',
  ADD_ATTACHMENT_BUCKET_KEY: 'ADD_ATTACHMENT_BUCKET_KEY',
  CHANGE_CURRENT_SESSION: 'CHANGE_CURRENT_SESSION',
} as const;
